import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { ArrowRight, Lock, X, ChevronDown, ChevronRight, BookOpen, BarChart3, Bell, Settings, FileText, Users, Shield, User, LogOut, Zap, Sun, Moon, CalendarDays } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { db } from "../firebase";

const PracticeHub = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAdmin, user } = useAuth() || {};
    const { theme, setTheme } = useTheme();
    const [selectedSubject, setSelectedSubject]   = useState(null);

    useEffect(() => {
        if (location.state?.selectedSubject) {
            const subj = subjects.find(s => s.id === location.state.selectedSubject);
            if (subj?.enabled) setSelectedSubject(subj);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const [activeTab, setActiveTab]               = useState("practice");
    const [showDropdown, setShowDropdown]         = useState(false);
    const [countdown, setCountdown]               = useState("");

    const [tabKey, setTabKey]                     = useState(0);
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);
    const [activeUsers, setActiveUsers]           = useState(31);
    const [showNotif, setShowNotif]               = useState(() => !localStorage.getItem("notifDismissed"));
    const [notifOpen, setNotifOpen]               = useState(false);
    const [officialSubTab, setOfficialSubTab]       = useState("tmua");
    const [showPricingModal, setShowPricingModal]   = useState(false);
    const [showSettings, setShowSettings]           = useState(false);
    const [showAvatarMenu, setShowAvatarMenu]       = useState(false);
    const [settingTimed, setSettingTimed]           = useState(true);
    const [settingClassic, setSettingClassic]       = useState(false);
    const [hoveredContingency, setHoveredContingency] = useState(null);
    const [sidebarCollapsed, setSidebarCollapsed]     = useState(false);
    const [perfKey, setPerfKey]                       = useState(0);
    const [communityQuestions, setCommunityQuestions] = useState([]);
    const [communityLoading, setCommunityLoading]     = useState(false);
    const [communityLoaded, setCommunityLoaded]       = useState(false);
    const [communityError, setCommunityError]         = useState("");

    const switchTab = (id) => { setActiveTab(id); setTabKey(k => k + 1); };

    const tabIcons = { practice: BookOpen, official: FileText, community: Users, analytics: BarChart3 };

    useEffect(() => {
        const hasVisited = localStorage.getItem("hasVisitedPracticeHub");
        if (!hasVisited) { setShowWelcomeModal(true); localStorage.setItem("hasVisitedPracticeHub", "true"); }
    }, []);

    useEffect(() => {
        let timer;
        const tick = () => {
            setActiveUsers(prev => Math.min(60, Math.max(20, prev + Math.floor(Math.random() * 5) - 2)));
            timer = setTimeout(tick, 7000 + Math.random() * 5000);
        };
        timer = setTimeout(tick, 7000 + Math.random() * 5000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const calc = () => {
            const diff = new Date("2026-10-14T09:00:00") - new Date();
            if (diff <= 0) { setCountdown({ days: 0, hours: 0, mins: 0, over: true }); return; }
            setCountdown({
                days:  Math.floor(diff / 86400000),
                hours: Math.floor((diff % 86400000) / 3600000),
                mins:  Math.floor((diff % 3600000) / 60000),
                over:  false,
            });
        };
        calc();
        const i = setInterval(calc, 60000);
        return () => clearInterval(i);
    }, []);

    const subjects = [
        { id: "tmua", name: "TMUA", fullName: "Test of Mathematics for University Admission", enabled: true },
        { id: "tara", name: "TARA", fullName: "Test of Abstract Reasoning and Aptitude", enabled: false },
        { id: "esat", name: "ESAT", fullName: "Engineering and Science Admissions Test", enabled: false },
    ];

    const tabs = [
        { id: "practice",  label: "Practice" },
        { id: "official",  label: "Official Papers" },
        { id: "community", label: "Community Papers" },
        { id: "analytics", label: "Performance" },
    ];

    const officialPapers = [
        { year: "2026", month: "January",  locked: true },
        { year: "2025", month: "October",  locked: true },
        { year: "2024",                    locked: true },
        { year: "2023", locked: false },
        { year: "2022", locked: false },
        { year: "2021", locked: false },
        { year: "2020", locked: false },
        { year: "2019", locked: false },
        { year: "2018", locked: false },
        { year: "2017", locked: false },
        { year: "2016", locked: false },
    ];

    const communitySets = useMemo(() => {
        const grouped = new Map();

        communityQuestions.forEach((question) => {
            const provider = question.communityProvider || "Community collection";
            const paper = question.communityPaper ?? "mixed";
            const key = `${provider}::${paper}`;
            if (!grouped.has(key)) grouped.set(key, { key, provider, paper, questions: [] });
            grouped.get(key).questions.push(question);
        });

        return Array.from(grouped.values())
            .map(set => ({
                ...set,
                questions: set.questions.sort((a, b) => (a.questionNumber ?? 0) - (b.questionNumber ?? 0)),
            }))
            .sort((a, b) => a.provider.localeCompare(b.provider) || String(a.paper).localeCompare(String(b.paper)));
    }, [communityQuestions]);

    useEffect(() => {
        if (activeTab !== "community" || communityLoaded) return;

        let cancelled = false;
        const fetchCommunityQuestions = async () => {
            setCommunityLoading(true);
            setCommunityError("");
            try {
                const snapshot = await getDocs(collection(db, "questions"));
                if (cancelled) return;
                const questions = snapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(question => question.communityProvider || question.questionType === "community" || question.questionType === "custom");
                setCommunityQuestions(questions);
                setCommunityLoaded(true);
            } catch (error) {
                if (!cancelled) setCommunityError("We couldn't load the community papers. Please try again.");
            } finally {
                if (!cancelled) setCommunityLoading(false);
            }
        };

        fetchCommunityQuestions();
        return () => { cancelled = true; };
    }, [activeTab, communityLoaded]);

    const updates = [
        { date: "22 Jan 2026", title: "Oxford Adopts TMUA",  description: "Oxford now requires TMUA for Mathematics and Computer Science from 2026 entry.", badge: "NEW" },
        { date: "11 Jan 2026", title: "Jan 2026 Paper Added", description: "The January 2026 sitting is now available in the Official Papers tab.", badge: null },
        { date: "13 Dec 2025", title: "Oct 2025 Paper Added", description: "The October 2025 paper with full worked solutions is now available.", badge: null },
    ];

    const performanceData = useMemo(() => {
        const raw = JSON.parse(localStorage.getItem("tmuaprep_sessions") || "[]");
        if (raw.length === 0) return { hasData: false };

        const totalCorrect  = raw.reduce((a, s) => a + (s.score    || 0), 0);
        const totalAnswered = raw.reduce((a, s) => a + (s.answered || s.total || 0), 0);
        const accuracy      = totalAnswered > 0 ? Math.round(totalCorrect / totalAnswered * 100) : 0;
        const avgGrade      = (raw.reduce((a, s) => a + parseFloat(s.grade || "0"), 0) / raw.length).toFixed(1);
        const bestPct       = Math.max(...raw.map(s => s.pct || 0));

        const uniqueDates = new Set(raw.map(s => s.date));
        const todayStr    = new Date().toISOString().split("T")[0];
        let streak = 0;
        const streakDay = new Date();
        if (!uniqueDates.has(todayStr)) streakDay.setDate(streakDay.getDate() - 1);
        while (uniqueDates.has(streakDay.toISOString().split("T")[0])) {
            streak++;
            streakDay.setDate(streakDay.getDate() - 1);
        }

        const totalMins = raw.reduce((a, s) => a + (s.totalTimeMins || 0), 0);
        const studyTime = totalMins === 0 ? "—"
            : Math.floor(totalMins / 60) > 0
                ? `${Math.floor(totalMins / 60)}h${totalMins % 60 > 0 ? " " + (totalMins % 60) + "m" : ""}`
                : `${totalMins}m`;

        const topicMap = {};
        raw.forEach(s => {
            Object.entries(s.topicResults || {}).forEach(([topic, { correct, answered }]) => {
                if (!topicMap[topic]) topicMap[topic] = { correct: 0, answered: 0 };
                topicMap[topic].correct  += correct;
                topicMap[topic].answered += answered;
            });
        });
        const topicBreakdown = Object.entries(topicMap)
            .map(([topic, { correct, answered }]) => ({ topic, done: correct, total: answered }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 8);

        const chartHistory = raw.slice(0, 7).reverse().map(s => ({
            label: new Date(s.date + "T12:00:00").toLocaleDateString("en-GB", { month: "short", day: "numeric" }),
            pct:   s.pct,
        }));

        const recentSessions = raw.slice(0, 6).map(s => ({
            paper:   s.paper,
            score:   `${s.score}/${s.total}`,
            pct:     s.pct,
            grade:   s.grade,
            avgTime: s.timed && s.avgTimeSecs > 0
                ? (s.avgTimeSecs >= 60 ? `${Math.floor(s.avgTimeSecs / 60)}m ${s.avgTimeSecs % 60}s` : `${s.avgTimeSecs}s`)
                : "—",
            date: new Date(s.date + "T12:00:00").toLocaleDateString("en-GB", { month: "short", day: "numeric" }),
        }));

        return {
            hasData: true,
            avgGrade,
            accuracy: `${accuracy}%`,
            correct: totalCorrect,
            incorrect: totalAnswered - totalCorrect,
            sessions: raw.length,
            bestPct: `${bestPct}%`,
            questionsCompleted: totalAnswered,
            streak,
            studyTime,
            chartHistory,
            topicBreakdown,
            recentSessions,
            recentIncorrect: [],
        };
    }, [perfKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const matPapers = [
        { year: "2023", label: "MAT 2023" },
        { year: "2023-contingency", label: "MAT 2023 Contingency", isContingency: true },
        { year: "2022", label: "MAT 2022" },
        { year: "2022-contingency", label: "MAT 2022 Contingency", isContingency: true },
        { year: "2021", label: "MAT 2021" },
        { year: "2021-contingency", label: "MAT 2021 Contingency", isContingency: true },
        { year: "2020", label: "MAT 2020" },
        { year: "2020-contingency", label: "MAT 2020 Contingency", isContingency: true },
        { year: "2019", label: "MAT 2019" },
        { year: "2018", label: "MAT 2018" },
        { year: "2017", label: "MAT 2017" },
        { year: "2016", label: "MAT 2016" },
        { year: "2015", label: "MAT 2015" },
        { year: "2014", label: "MAT 2014" },
        { year: "2013", label: "MAT 2013" },
        { year: "2012", label: "MAT 2012" },
        { year: "2011", label: "MAT 2011" },
        { year: "2010", label: "MAT 2010" },
        { year: "2009", label: "MAT 2009" },
        { year: "2008", label: "MAT 2008" },
        { year: "2007", label: "MAT 2007" },
        { year: "2006", label: "MAT 2006" },
        { year: "2005", label: "MAT 2005" },
        { year: "2004", label: "MAT 2004" },
        { year: "2003", label: "MAT 2003" },
        { year: "2002", label: "MAT 2002" },
        { year: "2001", label: "MAT 2001" },
        { year: "2000", label: "MAT 2000" },
        { year: "1998", label: "MAT 1998" },
        { year: "1997", label: "MAT 1997" },
        { year: "1996", label: "MAT 1996" },
    ];

    const pricingTiers = [
        {
            name: "Free", price: "£0", period: "forever",
            description: "Everything you need to get started.",
            features: ["20 questions per day from the bank", "All official TMUA past papers", "All MAT MCQ papers", "1:1 exam simulator", "Performance tracking"],
            cta: "Get started free", highlight: false,
        },
        {
            name: "Pro", price: "£19.99", period: "per month",
            description: "For students who want every advantage.",
            features: ["Everything in Free", "Full 1,000+ question bank", "Oct 2024, Oct 2025 & Jan 2026 papers", "Full worked solutions", "Spaced repetition mode"],
            cta: "Start free trial", highlight: true,
        },
    ];

    const handleSubjectClick = (s) => { if (s.enabled) { setSelectedSubject(s); setShowDropdown(false); } };
    const handleLockedClick  = () => setShowPricingModal(true);

    const NavBar = ({ right }) => (
        <nav className="border-b border-[var(--border-ui)] bg-[var(--bg)] px-6">
            <div className="py-3.5 flex items-center justify-between">
                <button onClick={() => navigate("/practice")} className="brand-btn">
                    tmua<span className="brand-prep">prep</span>
                </button>
                {right}
            </div>
        </nav>
    );

    const HeaderControls = () => (
        <div className="flex items-center gap-2.5">
            <div className="hidden lg:flex items-center gap-1.5">
                <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                </span>
                <span className="text-[var(--subtle-text)] text-xs">{activeUsers} practising</span>
            </div>

            {countdown && !countdown.over && (
                <div className="hidden sm:flex h-8 items-center gap-2 rounded-full border border-blue-400/20 bg-blue-500/[0.07] px-3 text-[11px] text-[var(--muted-text)] select-none">
                    <CalendarDays className="h-3.5 w-3.5 text-[var(--cobalt-light)]" strokeWidth={1.8} />
                    <span className="font-medium text-[var(--text-soft)]">TMUA</span>
                    <span className="h-3 w-px bg-blue-400/20" />
                    <span className="num font-semibold text-[var(--cobalt-light)]">{countdown.days} days</span>
                </div>
            )}

            <button
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-[var(--border-ui)] bg-[var(--surface)] text-[var(--muted-text)] transition hover:border-[var(--border-strong)] hover:text-[var(--text)]"
                aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
                title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            >
                {theme === "dark" ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
            </button>

            <div className="h-3.5 w-px bg-[var(--soft-fill-strong)]" />
            <button onClick={() => navigate("/")} className="text-[var(--subtle-text)] hover:text-[var(--text)] text-xs transition-colors font-medium">Home</button>
        </div>
    );

    /* ── Subject Selection ── */

    if (!selectedSubject) {
        return (
            <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex flex-col" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>
                <NavBar right={
                    <HeaderControls />
                } />
                <main className="paper-grid flex-1 px-6 py-12 md:py-16">
                    <div className="max-w-5xl mx-auto">
                        <div className="mb-8 text-center">
                            <h1 className="font-landing-display text-4xl leading-tight tracking-tight">Choose your exam</h1>
                            <p className="text-[var(--muted-text)] text-sm mt-2 leading-6">Open the practice hub for your admissions test.</p>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {subjects.map((s) => (
                                <button key={s.id} onClick={() => handleSubjectClick(s)} disabled={!s.enabled}
                                    style={{ backgroundColor: s.enabled ? "var(--surface)" : "var(--surface-raised)" }}
                                    className={`group relative flex min-h-[184px] w-full flex-col overflow-hidden rounded-2xl border p-5 text-left transition-all duration-200 ${s.enabled
                                        ? "border-blue-400/40 hover:-translate-y-0.5 hover:border-blue-400/70 hover:shadow-[var(--shadow)]"
                                        : "cursor-not-allowed border-[var(--border-ui)]"}`}>
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <div className={`text-2xl font-semibold tracking-tight ${s.enabled ? "text-[var(--text)]" : "text-[var(--muted-text)]"}`}>{s.name}</div>
                                            <div className={`mt-1.5 text-xs leading-5 ${s.enabled ? "text-[var(--text-soft)]" : "text-[var(--subtle-text)]"}`}>{s.fullName}</div>
                                        </div>
                                        <span className={`flex-shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-medium ${s.enabled
                                            ? "border-blue-400/25 bg-blue-500/[0.08] text-[var(--cobalt-light)]"
                                            : "border-[var(--border-ui)] bg-[var(--soft-fill)] text-[var(--subtle-text)]"}`}>
                                            {s.enabled ? "Available" : "Coming soon"}
                                        </span>
                                    </div>

                                    <div className="mt-auto flex w-full items-center justify-between gap-4 border-t border-[var(--border-ui)] pt-4">
                                        <span className={`text-[11px] ${s.enabled ? "text-[var(--muted-text)]" : "text-[var(--subtle-text)]"}`}>
                                            {s.enabled ? "1,000+ questions · papers · mock exams" : "Practice materials are being prepared"}
                                        </span>
                                        {s.enabled && (
                                            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-500 text-white transition-transform group-hover:translate-x-0.5">
                                                <ArrowRight className="w-3.5 h-3.5" />
                                            </span>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </main>
                <footer className="border-t border-[var(--border-ui)] px-6 py-5">
                    <p className="text-center text-[var(--subtle-text)] text-xs">&copy; 2025 TMUA Prep. All rights reserved.</p>
                </footer>
            </div>
        );
    }

    /* ── Subject Detail ── */
    return (
        <div className="h-screen bg-[var(--bg)] text-[var(--text)] flex flex-col overflow-hidden" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>

            {showWelcomeModal && (
                <div className="fixed inset-0 bg-[var(--overlay)] backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-[var(--surface)] border border-[var(--border-ui)] rounded-3xl max-w-sm w-full p-6 shadow-[var(--shadow)]">
                        <div className="flex items-center justify-between mb-5">
                            <span className="font-semibold text-sm tracking-tight">tmua<span className="text-[#60a5fa]">prep</span></span>
                            <button onClick={() => setShowWelcomeModal(false)} className="text-[var(--muted-text)] hover:text-[var(--text)] transition-colors"><X className="w-4 h-4" /></button>
                        </div>
                        <h3 className="text-base font-semibold text-[var(--text)] mb-2">Welcome to Practice Hub</h3>
                        <p className="text-xs text-[var(--subtle-text)] leading-relaxed mb-5">Start with the question bank, then try full mock papers. Track your progress in the Performance tab.</p>
                        <button onClick={() => setShowWelcomeModal(false)} className="btn-primary w-full">Let's go</button>
                    </div>
                </div>
            )}

            {/* Pricing Modal */}
            {showPricingModal && (
                <div className="fixed inset-0 bg-[var(--overlay)] backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-[backdropIn_0.2s_ease]" onClick={() => setShowPricingModal(false)}>
                    <div className="bg-[var(--surface)] border border-[var(--border-ui)] rounded-3xl max-w-lg w-full p-6 shadow-[var(--shadow)] animate-[scaleIn_0.22s_cubic-bezier(0.16,1,0.3,1)]" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-sm tracking-tight">tmua<span className="text-[#60a5fa]">prep</span></span>
                            <button onClick={() => setShowPricingModal(false)} className="text-[var(--muted-text)] hover:text-[var(--text)] transition-colors"><X className="w-4 h-4" /></button>
                        </div>
                        <p className="text-[10px] text-[var(--muted-text)] uppercase tracking-widest mb-5">Unlock recent papers</p>
                        <div className="grid grid-cols-2 gap-3 mb-5">
                            {pricingTiers.map(tier => (
                                <div key={tier.name} className={`relative rounded-xl border p-5 flex flex-col ${tier.highlight ? "border-[#60a5fa]/25 bg-[#60a5fa]/[0.03]" : "border-[var(--border-ui)]"}`}>
                                    {tier.highlight && (
                                        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 bg-[#60a5fa] text-white text-[10px] font-medium rounded-full whitespace-nowrap">
                                            Most popular
                                        </div>
                                    )}
                                    <div className="text-[10px] text-[var(--muted-text)] font-medium uppercase tracking-widest mb-2">{tier.name}</div>
                                    <div className="flex items-baseline gap-1 mb-1">
                                        <span className="text-xl font-semibold text-[var(--text)]">{tier.price}</span>
                                        <span className="text-[var(--muted-text)] text-xs">{tier.period}</span>
                                    </div>
                                    <p className="text-[var(--muted-text)] text-xs mb-4">{tier.description}</p>
                                    <ul className="space-y-1.5 mb-5 flex-1">
                                        {tier.features.map(f => (
                                            <li key={f} className="flex items-start gap-2 text-xs text-[var(--muted-text)]">
                                                <svg className="w-3 h-3 text-[#60a5fa] flex-shrink-0 mt-0.5" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                    <button className={tier.highlight ? "btn-primary w-full !py-2 !text-xs" : "btn-secondary w-full !py-2 !text-xs !text-[var(--text)]"}>
                                        {tier.cta}
                                    </button>
                                </div>
                            ))}
                        </div>
                        <p className="text-center text-[11px] text-[var(--subtle-text)]">Cancel any time. No commitment.</p>
                    </div>
                </div>
            )}

            {/* Header */}
            <header className="border-b border-[var(--border-ui)] bg-[var(--bg)] sticky top-0 z-40 px-6">
                <div className="py-3.5 flex items-center justify-between">
                    {/* LEFT: logo */}
                    <button onClick={() => { setSelectedSubject(null); navigate("/practice"); }} className="brand-btn">
                        tmua<span className="brand-prep">prep</span>
                    </button>

                    {/* RIGHT: live users, countdown, theme, home */}
                    <HeaderControls />
                </div>
            </header>

            <div className="flex flex-1 min-h-0 overflow-hidden">
                {/* ── Sidebar ── */}
                <div className="relative flex-shrink-0">
                <aside className={`${sidebarCollapsed ? 'w-[60px]' : 'w-[230px]'} transition-[width] duration-200 ease-in-out border-r border-[var(--border-ui)] flex flex-col h-full bg-[var(--surface)]`}>

                    {/* Subject switcher */}
                    <div className="border-b border-[var(--border-ui)] relative">
                        {sidebarCollapsed ? (
                            <div className="flex justify-center py-4">
                                <BookOpen className="w-4 h-4 text-[var(--subtle-text)]" strokeWidth={1.5} />
                            </div>
                        ) : (
                            <div className="px-4 py-3.5">
                                <button onClick={() => setShowDropdown(o => !o)}
                                    className="w-full flex items-center justify-between group">
                                    <div className="text-left">
                                        <div className="text-[10px] text-[var(--subtle-text)] uppercase tracking-widest mb-0.5 font-medium">Exam</div>
                                        <div className="text-sm font-semibold text-[var(--text)] group-hover:text-[var(--cobalt-light)] transition-colors">{selectedSubject.name}</div>
                                    </div>
                                    <ChevronDown className={`w-3.5 h-3.5 text-[var(--subtle-text)] transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                                </button>
                                {showDropdown && (
                                    <div className="absolute left-4 right-4 top-full mt-1 bg-[var(--surface-raised)] border border-[var(--border-ui)] rounded-xl overflow-hidden z-50 shadow-2xl" style={{ animation: "dropIn 0.16s cubic-bezier(0.16,1,0.3,1)" }}>
                                        {subjects.map(s => (
                                            <button key={s.id} onClick={() => handleSubjectClick(s)} disabled={!s.enabled}
                                                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all ${s.enabled ? "hover:bg-[var(--soft-fill)]" : "opacity-40 cursor-not-allowed"} ${s.id === selectedSubject.id ? "bg-[var(--soft-fill)]" : ""}`}>
                                                <div>
                                                    <div className="text-xs font-medium text-[var(--text)]">{s.name}</div>
                                                    <div className="text-[11px] text-[var(--subtle-text)]">{s.fullName}</div>
                                                </div>
                                                {!s.enabled && <span className="ml-auto text-[10px] text-[var(--subtle-text)]">Soon</span>}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Nav items */}
                    <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
                        {tabs.map(tab => {
                            const Icon = tabIcons[tab.id];
                            return (
                                <button key={tab.id} onClick={() => switchTab(tab.id)}
                                    title={sidebarCollapsed ? tab.label : undefined}
                                    className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-full text-sm transition-all ${
                                        activeTab === tab.id
                                            ? 'bg-[#60a5fa]/[0.14] text-[var(--cobalt-light)] shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]'
                                            : 'text-[var(--text-soft)] hover:text-[var(--text)] hover:bg-[var(--soft-fill)]'
                                    } ${sidebarCollapsed ? 'justify-center' : ''}`}>
                                    {activeTab === tab.id && !sidebarCollapsed && (
                                        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-[#60a5fa] rounded-full" />
                                    )}
                                    <Icon className={`w-[17px] h-[17px] flex-shrink-0 ${activeTab === tab.id ? 'text-[#60a5fa]' : ''}`} strokeWidth={1.5} />
                                    {!sidebarCollapsed && <span className="truncate font-medium">{tab.label}</span>}
                                </button>
                            );
                        })}
                    </nav>

                    {/* Bottom: notifications, settings, admin, avatar */}
                    <div className="border-t border-[var(--border-ui)] px-3 py-3 space-y-0.5">

                        {/* Notifications */}
                        <button onClick={() => { setNotifOpen(o => !o); setShowSettings(false); }}
                            title={sidebarCollapsed ? "Updates" : undefined}
                            className={`w-full flex items-center gap-3 px-2.5 py-2.5 rounded-lg transition-all ${notifOpen ? 'text-[var(--text)] bg-[var(--soft-fill)]' : 'text-[var(--text-soft)] hover:text-[var(--text)] hover:bg-[var(--soft-fill)]'} ${sidebarCollapsed ? 'justify-center' : ''}`}>
                            <div className="relative flex-shrink-0">
                                <Bell className="w-[17px] h-[17px]" strokeWidth={1.5} />
                                {showNotif && <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-[#60a5fa] rounded-full" />}
                            </div>
                            {!sidebarCollapsed && <span className="text-sm font-medium">Updates</span>}
                        </button>

                        {/* Settings */}
                        <button onClick={() => { setShowSettings(o => !o); setNotifOpen(false); }}
                            title={sidebarCollapsed ? "Settings" : undefined}
                            className={`w-full flex items-center gap-3 px-2.5 py-2.5 rounded-lg transition-all ${showSettings ? 'text-[var(--text)] bg-[var(--soft-fill)]' : 'text-[var(--text-soft)] hover:text-[var(--text)] hover:bg-[var(--soft-fill)]'} ${sidebarCollapsed ? 'justify-center' : ''}`}>
                            <Settings className="w-[17px] h-[17px] flex-shrink-0" strokeWidth={1.5} />
                            {!sidebarCollapsed && <span className="text-sm font-medium">Settings</span>}
                        </button>

                        {/* Admin */}
                        {isAdmin && (
                            <button onClick={() => navigate("/admin")}
                                title={sidebarCollapsed ? "Admin" : undefined}
                                className={`w-full flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-[#60a5fa] hover:bg-[#60a5fa]/[0.06] text-sm font-medium transition-all ${sidebarCollapsed ? 'justify-center' : ''}`}>
                                <Shield className="w-[17px] h-[17px] flex-shrink-0" strokeWidth={1.5} />
                                {!sidebarCollapsed && "Admin"}
                            </button>
                        )}

                        {/* Avatar */}
                        <div className="mt-1 pt-2 border-t border-[var(--border-ui)] relative">
                            <button onClick={() => setShowAvatarMenu(o => !o)} className={`w-full flex items-center rounded-lg hover:bg-[var(--soft-fill)] transition-all ${sidebarCollapsed ? 'justify-center p-2' : 'gap-2.5 px-2.5 py-2'}`}>
                                {user?.photoURL ? (
                                    <img src={user.photoURL} alt="" referrerPolicy="no-referrer" className="w-7 h-7 min-w-[28px] min-h-[28px] rounded-full flex-shrink-0 ring-1 ring-white/[0.12]" />
                                ) : (
                                    <div className="w-7 h-7 min-w-[28px] min-h-[28px] rounded-full bg-[var(--soft-fill)] border border-[var(--border-ui)] flex items-center justify-center flex-shrink-0">
                                        <span className="text-[10px] font-medium text-[var(--text-soft)]">{user?.displayName?.[0]?.toUpperCase() || 'U'}</span>
                                    </div>
                                )}
                                {!sidebarCollapsed && user && (
                                    <div className="min-w-0 text-left">
                                        <div className="text-xs font-medium text-[var(--text)] truncate">{user.displayName}</div>
                                        <div className="text-[10px] text-[var(--subtle-text)] truncate">{user.email}</div>
                                    </div>
                                )}
                            </button>
                            {showAvatarMenu && (
                                <>
                                <div className="fixed inset-0 z-40" onClick={() => setShowAvatarMenu(false)} />
                                <div className="absolute bottom-full mb-2 left-0 w-60 bg-[var(--surface-raised)] border border-[var(--border-ui)] rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.6)] overflow-hidden z-50" style={{ animation: "dropIn 0.16s cubic-bezier(0.16,1,0.3,1)" }}>
                                    {/* User info header */}
                                    <div className="px-4 py-4">
                                        <div className="flex items-center gap-3 mb-0.5">
                                            {user?.photoURL ? (
                                                <img src={user.photoURL} alt="" referrerPolicy="no-referrer" className="w-8 h-8 rounded-full flex-shrink-0 ring-1 ring-white/[0.1]" />
                                            ) : (
                                                <div className="w-8 h-8 rounded-full bg-[var(--soft-fill)] border border-[var(--border-ui)] flex items-center justify-center flex-shrink-0">
                                                    <span className="text-[11px] font-medium text-[var(--text-soft)]">{user?.displayName?.[0]?.toUpperCase() || 'U'}</span>
                                                </div>
                                            )}
                                            <div className="min-w-0">
                                                <div className="text-[13px] font-semibold text-[var(--text)] truncate leading-tight">{user?.displayName}</div>
                                                <div className="text-[11px] text-[var(--subtle-text)] truncate leading-tight mt-0.5">{user?.email}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="h-px bg-[var(--soft-fill)] mx-0" />
                                    {/* Menu items */}
                                    <div className="px-1.5 py-1.5 space-y-px">
                                        <button
                                            onClick={() => setShowAvatarMenu(false)}
                                            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-[var(--text-soft)] hover:text-[var(--text)] hover:bg-[var(--soft-fill)] transition-colors"
                                        >
                                            <User className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.5} />
                                            Account
                                        </button>
                                        <button
                                            onClick={() => { setShowAvatarMenu(false); setShowPricingModal(true); }}
                                            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-[#60a5fa] hover:bg-[#60a5fa]/[0.07] transition-colors"
                                        >
                                            <Zap className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.5} />
                                            Upgrade to Pro
                                        </button>
                                    </div>
                                    <div className="h-px bg-[var(--soft-fill)]" />
                                    <div className="px-1.5 py-1.5">
                                        <button
                                            onClick={() => setShowAvatarMenu(false)}
                                            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-[var(--subtle-text)] hover:text-[var(--text)] hover:bg-[var(--soft-fill)] transition-colors"
                                        >
                                            <LogOut className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.5} />
                                            Log out
                                        </button>
                                    </div>
                                </div>
                                </>
                            )}
                        </div>
                    </div>
                </aside>

                {/* Collapse toggle */}
                <button onClick={() => setSidebarCollapsed(c => !c)}
                    title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-20 w-[22px] h-[22px] rounded-full bg-[var(--surface-raised)] border border-[var(--border-ui)] flex items-center justify-center text-[var(--muted-text)] hover:text-[var(--text)] hover:bg-[var(--surface-elevated)] hover:border-[var(--border-ui)] shadow-[0_2px_8px_rgba(0,0,0,0.5)] transition-all duration-150">
                    <ChevronRight className={`w-3 h-3 transition-transform duration-200 ${sidebarCollapsed ? '' : 'rotate-180'}`} strokeWidth={2} />
                </button>
                </div>

                {/* ── Main content ── */}
                <main className="flex-1 overflow-auto px-6 py-8 md:px-9">
                <div key={tabKey} style={{ animation: "tabSlideIn 0.18s ease" }}>

                    {/* ── Practice Tab ── */}
                    {activeTab === "practice" && (
                        <div className="grid lg:grid-cols-5 gap-4 max-w-4xl items-stretch">

                            {/* Question Bank — dominant panel */}
                            <button onClick={() => navigate("/question-config")}
                                className="group relative lg:col-span-3 min-h-[280px] text-left rounded-3xl overflow-hidden bg-[var(--surface)] border border-[var(--border-ui)] hover:border-[#60a5fa]/[0.4] p-8 flex flex-col transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[var(--shadow)]">
                                {/* oversized watermark icon */}
                                <BookOpen className="absolute -right-8 -bottom-8 w-48 h-48 text-[#60a5fa]/[0.05] group-hover:text-[#60a5fa]/[0.09] group-hover:rotate-[-4deg] transition-all duration-500" strokeWidth={0.75} />

                                <h3 className="text-2xl font-bold tracking-tight text-[var(--text)]">Question Bank</h3>
                                <p className="text-[13px] text-[var(--muted-text)] leading-relaxed mt-3 max-w-sm">
                                    Build a focused practice set from the full TMUA question bank.
                                </p>

                                <div className="mt-auto flex items-end justify-between gap-4">
                                    <div>
                                        <div className="num text-xl font-semibold text-[var(--text)] leading-none">1,000+</div>
                                        <div className="text-[10px] text-[var(--subtle-text)] mt-1">questions</div>
                                    </div>
                                    <span className="btn-primary !py-2.5 pointer-events-none">
                                        Choose questions <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                                    </span>
                                </div>
                            </button>

                            {/* Mock Exam */}
                            <button onClick={() => navigate("/exam-select")}
                                className="group relative lg:col-span-2 min-h-[280px] text-left rounded-3xl overflow-hidden bg-[var(--surface)] border border-[var(--border-ui)] hover:border-[#60a5fa]/[0.35] p-8 flex flex-col transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[var(--shadow)]">
                                <h3 className="text-xl font-bold tracking-tight text-[var(--text)]">Mock Exam</h3>
                                <p className="text-[13px] text-[var(--muted-text)] leading-relaxed mt-3 max-w-xs">
                                    Sit a complete timed paper under realistic exam conditions.
                                </p>

                                <span className="btn-primary mt-auto self-start !py-2.5 pointer-events-none">
                                    Start mock <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                                </span>
                            </button>
                        </div>
                    )}

                    {/* ── Official Papers ── */}
                    {activeTab === "official" && (
                        <div className="max-w-4xl">

                            {/* Segmented pill sub-tabs */}
                            <div className="inline-flex items-center bg-[var(--surface)] border border-[var(--border-ui)] rounded-full p-1 mb-7">
                                {[{ id: "tmua", label: "TMUA" }, { id: "mat", label: "MAT Section A" }].map(st => (
                                    <button key={st.id} onClick={() => setOfficialSubTab(st.id)}
                                        className={`text-xs font-medium px-4 py-2 rounded-full transition-all duration-200 ${officialSubTab === st.id ? "bg-[#60a5fa]/[0.18] text-[var(--cobalt-light)] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]" : "text-[var(--subtle-text)] hover:text-[var(--text-soft)]"}`}>
                                        {st.label}
                                    </button>
                                ))}
                            </div>

                            {/* TMUA Papers — booklet covers */}
                            {officialSubTab === "tmua" && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                    {officialPapers.map((paper, i) => (
                                        <button key={paper.year}
                                            onClick={paper.locked ? handleLockedClick : () => navigate("/exam-select", { state: { year: paper.year.match(/\d{4}/)?.[0], includeOfficial: true, includeCustom: false } })}
                                            style={{ animation: "slideUp 0.35s ease both", animationDelay: `${i * 30}ms` }}
                                            className={`group relative text-left rounded-xl overflow-hidden border transition-all duration-200 ${paper.locked
                                                ? "bg-[var(--surface)] border-[var(--border-ui)] hover:border-[#60a5fa]/[0.38]"
                                                : "bg-[var(--surface-raised)] border-[var(--border-ui)] hover:border-[#60a5fa]/[0.45] hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(2,4,12,0.7),0_0_20px_rgba(96,165,250,0.08)]"}`}>

                                            {/* booklet spine */}
                                            <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${paper.locked ? "bg-[#60a5fa]/[0.3]" : "bg-gradient-to-b from-[#60a5fa] to-[#3b82f6]"}`} />

                                            <div className="p-4 pl-5">
                                                <div className="flex items-start justify-between mb-5">
                                                    <span className={`text-[10px] font-medium ${paper.locked ? "text-[var(--faint-text)]" : "text-[var(--subtle-text)]"}`}>UAT-UK · Official</span>
                                                    {paper.locked && (
                                                        <span className="flex items-center gap-1 text-[9px] font-semibold text-[#60a5fa] bg-[#60a5fa]/[0.08] border border-[#60a5fa]/[0.25] px-2 py-0.5 rounded-full select-none">
                                                            <Lock className="w-2.5 h-2.5" /> Pro
                                                        </span>
                                                    )}
                                                </div>
                                                <div className={`text-[32px] font-semibold leading-none mb-0.5 ${paper.locked ? "text-[var(--faint-text)] group-hover:text-[var(--subtle-text)]" : "text-[var(--text)] group-hover:text-[#3b82f6]"} transition-colors duration-200`}>
                                                    {paper.year.match(/\d{4}/)?.[0]}
                                                </div>
                                                <div className={`text-[11px] mb-4 ${paper.locked ? "text-[var(--subtle-text)]" : "text-[var(--muted-text)]"}`}>{paper.month || "October"}</div>
                                                <div className="flex items-center justify-between text-[10px] text-[var(--subtle-text)]">
                                                    <span>P1 &amp; P2 · 40 questions · 150 min</span>
                                                    {!paper.locked && <ArrowRight className="w-3 h-3 text-[var(--faint-text)] group-hover:text-[var(--cobalt-light)] group-hover:translate-x-0.5 transition-all duration-200" />}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* MAT Papers — booklet covers */}
                            {officialSubTab === "mat" && (
                                <div>
                                    <div className="rounded-xl border border-[var(--border-ui)] bg-[var(--soft-fill)] px-4 py-3 mb-5 max-w-2xl">
                                        <p className="text-xs text-[var(--muted-text)] leading-relaxed">
                                            MAT Section A was a multiple-choice section included in papers up to 2023. These are excellent supplementary practice at a comparable TMUA difficulty level.
                                            {" "}<span className="text-[var(--subtle-text)]">Some topics may fall outside the TMUA specification.</span>
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                        {matPapers.map((paper, i) => (
                                            <button key={paper.year}
                                                onClick={() => {
                                                    const yearNum = paper.year.replace("-contingency", "");
                                                    navigate("/exam-select", { state: {
                                                        year: yearNum,
                                                        paper: "section-a",
                                                        examType: "mat",
                                                        includeOfficial: !paper.isContingency,
                                                        includeContingency: paper.isContingency || false,
                                                        includeCustom: false,
                                                    }});
                                                }}
                                                style={{ animation: "slideUp 0.35s ease both", animationDelay: `${Math.min(i * 25, 400)}ms` }}
                                                className="group relative text-left rounded-xl overflow-hidden bg-[var(--surface-raised)] border border-[var(--border-ui)] hover:border-[#60a5fa]/[0.45] hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(2,4,12,0.7),0_0_20px_rgba(96,165,250,0.08)] transition-all duration-200">
                                                <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-[#8791ab]/60 to-[#525b73]/60" />
                                                <div className="p-4 pl-5">
                                                    <div className="flex items-start justify-between mb-5">
                                                        <span className="text-[10px] font-medium text-[var(--subtle-text)]">Oxford · MAT</span>
                                                        {paper.isContingency && (
                                                            <span
                                                                onMouseEnter={() => setHoveredContingency(paper.year)}
                                                                onMouseLeave={() => setHoveredContingency(null)}
                                                                onClick={e => e.stopPropagation()}
                                                                className="relative text-[9px] font-medium text-[var(--muted-text)] bg-[var(--soft-fill)] border border-[var(--border-ui)] px-2 py-0.5 rounded-full select-none cursor-default">
                                                                Contingency
                                                                {hoveredContingency === paper.year && (
                                                                    <span className="absolute right-0 top-full mt-1.5 w-56 bg-[var(--surface-hover)] border border-[var(--border-ui)] rounded-lg px-3 py-2.5 z-20 shadow-xl pointer-events-none block normal-case tracking-normal font-sans">
                                                                        <span className="text-[11px] text-[var(--muted-text)] leading-relaxed block text-left">
                                                                            A backup assessment for students with extenuating circumstances. 10 MCQs, slightly harder than the standard paper.
                                                                        </span>
                                                                    </span>
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-[32px] font-semibold leading-none mb-0.5 text-[var(--text)] group-hover:text-[var(--cobalt-light)] transition-colors duration-200">
                                                        {paper.year.replace("-contingency", "")}
                                                    </div>
                                                    <div className="text-[11px] text-[var(--muted-text)] mb-4">{paper.isContingency ? "Contingency sitting" : "Standard sitting"}</div>
                                                    <div className="flex items-center justify-between text-[10px] text-[var(--subtle-text)]">
                                                        <span>Section A · multiple choice · 60 min</span>
                                                        <ArrowRight className="w-3 h-3 text-[var(--faint-text)] group-hover:text-[var(--cobalt-light)] group-hover:translate-x-0.5 transition-all duration-200" />
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                        </div>
                    )}

                    {/* ── Community Papers ── */}
                    {activeTab === "community" && (
                        <div className="max-w-4xl">
                            <div className="mb-6">
                                <h2 className="font-display text-3xl tracking-tight text-[var(--text)]">Community papers</h2>
                                <p className="text-sm text-[var(--muted-text)] mt-1">Practice complete papers contributed by the community.</p>
                            </div>

                            {communityLoading && (
                                <div className="rounded-2xl border border-[var(--border-ui)] bg-[var(--surface)] px-6 py-12 text-center text-sm text-[var(--subtle-text)]">
                                    Loading community papers…
                                </div>
                            )}

                            {communityError && (
                                <div className="rounded-2xl border border-red-400/20 bg-red-400/[0.04] px-6 py-8 text-center">
                                    <p className="text-sm text-[var(--text-soft)]">{communityError}</p>
                                    <button onClick={() => { setCommunityLoaded(false); setCommunityError(""); }} className="btn-secondary !py-2 mt-4">Try again</button>
                                </div>
                            )}

                            {!communityLoading && !communityError && communityLoaded && communitySets.length === 0 && (
                                <div className="rounded-2xl border border-[var(--border-ui)] bg-[var(--surface)] px-6 py-12 text-center">
                                    <p className="text-sm font-medium text-[var(--text)]">No community papers yet</p>
                                    <p className="text-xs text-[var(--subtle-text)] mt-1">New contributed papers will appear here automatically.</p>
                                </div>
                            )}

                            {!communityLoading && !communityError && communitySets.length > 0 && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                    {communitySets.map((set, index) => (
                                        <button key={set.key}
                                            onClick={() => navigate("/exam-select", { state: set.provider === "Community collection"
                                                ? { includeOfficial: false, includeCustom: true, includeCommunity: true, timedMode: false }
                                                : { communityProvider: set.provider, ...(set.paper === "mixed" ? {} : { communityPaper: set.paper }) }
                                            })}
                                            style={{ animation: "slideUp 0.35s ease both", animationDelay: `${index * 35}ms` }}
                                            className="group relative min-h-[164px] text-left rounded-xl overflow-hidden bg-[var(--surface-raised)] border border-[var(--border-ui)] hover:border-[#60a5fa]/[0.45] hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(2,4,12,0.7),0_0_20px_rgba(96,165,250,0.08)] transition-all duration-200">
                                            <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-[#60a5fa] to-[#3b82f6]" />
                                            <div className="h-full p-4 pl-5 flex flex-col">
                                                <div className="text-[10px] font-medium text-[var(--subtle-text)] mb-5">{set.provider} · Community</div>
                                                <div className="text-[26px] font-semibold leading-none text-[var(--text)] group-hover:text-[var(--cobalt-light)] transition-colors duration-200">
                                                    {set.paper === "mixed" ? "Collection" : `Paper ${set.paper}`}
                                                </div>
                                                <div className="mt-auto pt-5 flex items-center justify-between text-[10px] text-[var(--subtle-text)]">
                                                    <span>{set.questions.length} question{set.questions.length === 1 ? "" : "s"}</span>
                                                    <ArrowRight className="w-3 h-3 text-[var(--faint-text)] group-hover:text-[var(--cobalt-light)] group-hover:translate-x-0.5 transition-all duration-200" />
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Performance Tab ── */}
                    {activeTab === "analytics" && (
                        <>
                            {performanceData.hasData ? (
                                <div className="space-y-3 max-w-5xl" style={{ animation: "tabSlideIn 0.3s ease both" }}>

                                    {/* Admin reset button */}
                                    {isAdmin && (
                                        <div className="flex justify-end">
                                            <button
                                                onClick={() => { if (window.confirm("Reset all performance data?")) { localStorage.removeItem("tmuaprep_sessions"); setPerfKey(k => k + 1); } }}
                                                className="text-[11px] text-[var(--subtle-text)] hover:text-red-400 transition-colors px-2 py-1 rounded hover:bg-red-400/[0.06]"
                                            >
                                                Reset data
                                            </button>
                                        </div>
                                    )}

                                    {/* Primary stat cards */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        {[
                                            { label: "Avg Score", value: performanceData.avgGrade + " / 9", sub: "TMUA grade",      primary: true },
                                            { label: "Accuracy",  value: performanceData.accuracy,           sub: `${performanceData.correct} correct` },
                                            { label: "Sessions",   value: `${performanceData.sessions}`,  sub: "completed" },
                                            { label: "Best Score", value: performanceData.bestPct,           sub: "single paper" },
                                        ].map((s, i) => (
                                            <div key={s.label}
                                                className="group relative bg-[var(--surface-raised)] border border-[var(--border-ui)] hover:border-[#60a5fa]/[0.18] rounded-xl px-5 pt-4 pb-5 transition-all duration-200 hover:-translate-y-0.5 cursor-default overflow-hidden"
                                                style={{ animation: "slideUp 0.4s ease both", animationDelay: `${i * 55}ms` }}
                                                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 8px 28px rgba(0,0,0,0.4)"}
                                                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                                            >
                                                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-[#60a5fa]/[0.15] to-transparent" />
                                                <div className="eyebrow text-[var(--subtle-text)] mb-3">{s.label}</div>
                                                <div className={`num text-[30px] font-semibold leading-none mb-2 ${s.primary ? "text-[#60a5fa]" : "text-[var(--text)]"}`}>{s.value}</div>
                                                <div className="text-xs text-[var(--subtle-text)]">{s.sub}</div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Secondary stats */}
                                    <div className="grid grid-cols-3 gap-3">
                                        {[
                                            { label: "Day Streak", value: `${performanceData.streak}`, unit: "days", sub: "keep it up" },
                                            { label: "Study Time", value: performanceData.studyTime,   unit: "",      sub: "total practice" },
                                            { label: "Completed",  value: `${performanceData.questionsCompleted}`, unit: "", sub: "questions answered" },
                                        ].map((s, i) => (
                                            <div key={s.label}
                                                className="bg-[var(--surface-raised)] border border-[var(--border-ui)] hover:border-[#60a5fa]/[0.18] rounded-xl px-5 py-4 transition-all duration-200 hover:-translate-y-0.5 cursor-default"
                                                style={{ animation: "slideUp 0.4s ease both", animationDelay: `${220 + i * 55}ms` }}
                                                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 8px 28px rgba(0,0,0,0.4)"}
                                                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                                            >
                                                <div className="eyebrow text-[var(--subtle-text)] mb-2">{s.label}</div>
                                                <div className="num text-2xl font-semibold text-[var(--text)] leading-none">
                                                    {s.value}<span className="text-xs text-[var(--subtle-text)] ml-1.5 font-normal">{s.unit}</span>
                                                </div>
                                                <div className="text-xs text-[var(--subtle-text)] mt-1">{s.sub}</div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Score trend chart */}
                                    <div className="bg-[var(--surface-raised)] border border-[var(--border-ui)] rounded-xl overflow-hidden" style={{ animation: "slideUp 0.4s ease both", animationDelay: "380ms" }}>
                                        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-ui)]">
                                            <div>
                                                <div className="eyebrow text-[var(--subtle-text)]">Score Trend</div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="flex items-center gap-1.5">
                                                    <div className="w-2 h-2 rounded-full bg-[#60a5fa]" />
                                                    <span className="text-[11px] text-[var(--subtle-text)]">Accuracy %</span>
                                                </div>
                                                <span className="text-[10px] text-[var(--faint-text)] border border-[var(--border-ui)] px-2 py-0.5 rounded">Last 7 sessions</span>
                                            </div>
                                        </div>
                                        <div className="px-6 py-5">
                                            {(() => {
                                                const pts = performanceData.chartHistory;
                                                const W = 600, H = 130;
                                                const padL = 36, padR = 16, padT = 12, padB = 28;
                                                const xScale = i => pts.length > 1 ? padL + (i / (pts.length - 1)) * (W - padL - padR) : padL + (W - padL - padR) / 2;
                                                const yMin = 0, yMax = 100;
                                                const yScale = v => H - padB - ((v - yMin) / (yMax - yMin)) * (H - padT - padB);
                                                const linePath = pts.map((p, i) => `${i === 0 ? "M" : "L"}${xScale(i).toFixed(1)},${yScale(p.pct).toFixed(1)}`).join(" ");
                                                const areaPath = `${linePath} L${xScale(pts.length - 1).toFixed(1)},${(H - padB).toFixed(1)} L${xScale(0).toFixed(1)},${(H - padB).toFixed(1)} Z`;
                                                return (
                                                    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 150 }}>
                                                        <defs>
                                                            <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.22" />
                                                                <stop offset="100%" stopColor="#60a5fa" stopOpacity="0" />
                                                            </linearGradient>
                                                        </defs>
                                                        {[25, 50, 75].map(v => (
                                                            <g key={v}>
                                                                <line x1={padL} x2={W - padR} y1={yScale(v)} y2={yScale(v)} stroke="rgba(255,255,255,0.035)" strokeWidth="1" strokeDasharray="3 4" />
                                                                <text x={padL - 6} y={yScale(v) + 3.5} fontSize="9" fill="#414960" textAnchor="end">{v}%</text>
                                                            </g>
                                                        ))}
                                                        <path d={areaPath} fill="url(#scoreGrad)" />
                                                        <path d={linePath} fill="none" stroke="#60a5fa" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        {pts.map((p, i) => (
                                                            <g key={i} className="group/dot" style={{ cursor: "default" }}>
                                                                <circle cx={xScale(i)} cy={yScale(p.pct)} r="8" fill="transparent" />
                                                                <circle cx={xScale(i)} cy={yScale(p.pct)} r="3" fill="#121828" stroke="#60a5fa" strokeWidth="1.5" />
                                                                <circle cx={xScale(i)} cy={yScale(p.pct)} r="6" fill="none" stroke="#60a5fa" strokeWidth="1" strokeOpacity="0">
                                                                    <animate attributeName="strokeOpacity" values="0;0.3;0" dur="2s" begin={`${i * 0.3}s`} repeatCount="indefinite" />
                                                                    <animate attributeName="r" values="3;7;3" dur="2s" begin={`${i * 0.3}s`} repeatCount="indefinite" />
                                                                </circle>
                                                                <text x={xScale(i)} y={H - 8} fontSize="9" fill="#414960" textAnchor="middle">{p.label}</text>
                                                            </g>
                                                        ))}
                                                    </svg>
                                                );
                                            })()}
                                        </div>
                                    </div>

                                    {/* Bottom two-col: recent sessions + topic breakdown */}
                                    <div className="grid grid-cols-5 gap-3">

                                        {/* Recent sessions */}
                                        <div className="col-span-3 bg-[var(--surface-raised)] border border-[var(--border-ui)] rounded-xl overflow-hidden" style={{ animation: "slideUp 0.4s ease both", animationDelay: "440ms" }}>
                                            <div className="px-6 py-4 border-b border-[var(--border-ui)]">
                                                <div className="eyebrow text-[var(--subtle-text)]">Recent Sessions</div>
                                            </div>
                                            <div className="grid grid-cols-5 px-6 py-2.5 border-b border-[var(--border-ui)]">
                                                {["Paper", "Score", "Grade", "Avg Time", "Date"].map(h => (
                                                    <div key={h} className="eyebrow text-[var(--faint-text)]">{h}</div>
                                                ))}
                                            </div>
                                            {performanceData.recentSessions.map((s, i) => (
                                                <div key={i}
                                                    className="group grid grid-cols-5 items-center px-6 py-3.5 border-b border-[var(--border-ui)] last:border-0 transition-all duration-150 hover:bg-[var(--soft-fill)] cursor-default relative"
                                                    style={{ animation: "slideUp 0.35s ease both", animationDelay: `${500 + i * 40}ms` }}
                                                >
                                                    <div className="absolute left-0 top-0 bottom-0 w-px bg-[#60a5fa] opacity-0 group-hover:opacity-100 transition-opacity duration-150" />
                                                    <div className="text-xs text-[var(--text-soft)] truncate pr-2 group-hover:text-[var(--text)] transition-colors">{s.paper}</div>
                                                    <div className="num text-xs font-medium text-[var(--text)]">{s.score}</div>
                                                    <div className="num text-xs font-medium text-[#60a5fa]">{s.grade}</div>
                                                    <div className="num text-[11px] text-[var(--subtle-text)]">{s.avgTime}</div>
                                                    <div className="num text-[11px] text-[var(--faint-text)]">{s.date}</div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Topic breakdown */}
                                        <div className="col-span-2 bg-[var(--surface-raised)] border border-[var(--border-ui)] rounded-xl overflow-hidden" style={{ animation: "slideUp 0.4s ease both", animationDelay: "480ms" }}>
                                            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-ui)]">
                                                <div className="eyebrow text-[var(--subtle-text)]">By Topic</div>
                                                <button onClick={() => navigate("/question-config")} className="text-[11px] text-[#60a5fa] hover:text-[var(--text)] transition-colors font-medium">Drill →</button>
                                            </div>
                                            <div className="px-5 py-4 space-y-4">
                                                {performanceData.topicBreakdown.map(({ topic, done, total }, i) => {
                                                    const pct = Math.round((done / total) * 100);
                                                    return (
                                                        <div key={topic} className="group/bar" style={{ animation: "slideUp 0.35s ease both", animationDelay: `${540 + i * 40}ms` }}>
                                                            <div className="flex items-center justify-between mb-1.5">
                                                                <span className="text-xs text-[var(--subtle-text)] group-hover/bar:text-[var(--text-soft)] transition-colors">{topic}</span>
                                                                <span className="text-[11px] text-[var(--subtle-text)] tabular-nums">{pct}%</span>
                                                            </div>
                                                            <div className="w-full h-px bg-[var(--soft-fill)] rounded-full overflow-hidden">
                                                                <div className="h-full rounded-full bg-[#60a5fa]/50 group-hover/bar:bg-[#60a5fa]/70 transition-colors duration-200"
                                                                    style={{ width: `${pct}%`, animation: `barFill 0.8s ease both`, animationDelay: `${580 + i * 60}ms`, "--bar-pct": `${pct}%` }} />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Recent Incorrect Questions */}
                                    <div className="bg-[var(--surface-raised)] border border-[var(--border-ui)] rounded-xl overflow-hidden" style={{ animation: "slideUp 0.4s ease both", animationDelay: "540ms" }}>
                                        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-ui)]">
                                            <div className="eyebrow text-[var(--subtle-text)]">Review Incorrect</div>
                                            <span className="text-[10px] text-[var(--subtle-text)] bg-[var(--soft-fill)] border border-[var(--border-ui)] px-2.5 py-1 rounded-md tracking-wide">Coming soon</span>
                                        </div>
                                        <div className="divide-y divide-[var(--border-ui)]">
                                            {performanceData.recentIncorrect.map((q, i) => (
                                                <div key={i} className="flex items-center justify-between px-6 py-3.5 opacity-30 select-none">
                                                    <div>
                                                        <div className="text-xs text-[var(--text)] mb-0.5">{q.question}</div>
                                                        <div className="text-[11px] text-[var(--subtle-text)]">{q.topic}</div>
                                                    </div>
                                                    <div className="text-[11px] text-[var(--subtle-text)]">{q.date}</div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="px-6 py-3.5 border-t border-[var(--border-ui)]">
                                            <p className="text-[11px] text-[var(--faint-text)]">Full review mode — practise your missed questions — launching soon.</p>
                                        </div>
                                    </div>

                                </div>
                            ) : (
                                <div className="border border-[var(--border-ui)] rounded-xl p-16 text-center max-w-md mx-auto" style={{ animation: "scaleIn 0.3s ease both" }}>
                                    <div className="w-14 h-14 rounded-2xl bg-[var(--soft-fill)] border border-[var(--border-ui)] flex items-center justify-center mx-auto mb-5">
                                        <BarChart3 className="w-6 h-6 text-[var(--faint-text)]" strokeWidth={1.5} />
                                    </div>
                                    <h3 className="text-sm font-semibold text-[var(--text)] mb-1.5">No data yet</h3>
                                    <p className="text-xs text-[var(--subtle-text)] mb-6 leading-relaxed">Complete practice sessions to see your score trends, topic breakdown, and ranking.</p>
                                    <button onClick={() => setActiveTab("practice")}
                                        className="px-5 py-2 bg-[var(--surface-raised)] hover:bg-[var(--surface-elevated)] border border-[#60a5fa]/[0.2] hover:border-[#60a5fa]/[0.4] text-[#60a5fa] text-xs font-medium rounded-lg transition-all duration-150 hover:-translate-y-px">
                                        Start practising
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
                </main>
            </div>

            {/* ── Updates Panel ── */}
            {notifOpen && (
                <div className="fixed inset-0 z-50 flex justify-end">
                    <div className="absolute inset-0 bg-[var(--overlay)] backdrop-blur-[2px]" onClick={() => setNotifOpen(false)} />
                    <div className="relative w-80 h-full bg-[var(--surface)] border-l border-[var(--border-ui)] flex flex-col shadow-2xl" style={{ animation: "slideInRight 0.22s cubic-bezier(0.16,1,0.3,1)" }}>
                        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-ui)]">
                            <div>
                                <div className="text-sm font-semibold text-[var(--text)]">What's new</div>
                                <div className="text-[11px] text-[var(--subtle-text)] mt-0.5">Latest updates to tmuaprep</div>
                            </div>
                            <button onClick={() => { setNotifOpen(false); setShowNotif(false); localStorage.setItem("notifDismissed", "1"); }} className="w-7 h-7 rounded-md flex items-center justify-center text-[var(--subtle-text)] hover:text-[var(--text)] hover:bg-[var(--soft-fill)] transition-colors">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {updates.map((u, i) => (
                                <div key={i} className="px-5 py-4 border-b border-[var(--border-ui)] flex gap-3.5">
                                    <div className="w-1.5 h-1.5 rounded-full bg-[#60a5fa]/50 mt-[5px] flex-shrink-0" />
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <span className="text-[13px] font-medium text-[var(--text)]">{u.title}</span>
                                            {u.badge && <span className="px-1.5 py-px rounded text-[9px] font-semibold bg-[#60a5fa]/[0.12] text-[#60a5fa] uppercase tracking-wide border border-[#60a5fa]/[0.2]">{u.badge}</span>}
                                        </div>
                                        <p className="text-[12px] text-[var(--muted-text)] leading-relaxed">{u.description}</p>
                                        <p className="text-[10px] text-[var(--faint-text)] mt-2 font-medium">{u.date}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Settings Panel ── */}
            {showSettings && (
                <div className="fixed inset-0 z-50 flex justify-end">
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-[var(--overlay)] backdrop-blur-[2px]" onClick={() => setShowSettings(false)} />
                    {/* Panel */}
                    <div className="relative w-80 h-full bg-[var(--surface)] border-l border-[var(--border-ui)] flex flex-col shadow-2xl" style={{ animation: "slideInRight 0.22s cubic-bezier(0.16,1,0.3,1)" }}>
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-ui)]">
                            <div>
                                <div className="text-sm font-semibold text-[var(--text)]">Settings</div>
                                <div className="text-[11px] text-[var(--subtle-text)] mt-0.5">Preferences &amp; display</div>
                            </div>
                            <button onClick={() => setShowSettings(false)} className="w-7 h-7 rounded-full flex items-center justify-center text-[var(--subtle-text)] hover:text-[var(--text)] hover:bg-[var(--soft-fill-strong)] transition-colors">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        {/* Content */}
                        <div className="flex-1 overflow-y-auto px-5 py-5">
                            <p className="text-[10px] text-[var(--subtle-text)] tracking-wide font-medium mb-3">Appearance</p>
                            <div className="grid grid-cols-2 gap-2 rounded-2xl border border-[var(--border-ui)] bg-[var(--surface-raised)] p-2">
                                {[
                                    { id: "light", label: "Light", Icon: Sun },
                                    { id: "dark", label: "Dark", Icon: Moon },
                                ].map(({ id, label, Icon }) => (
                                    <button
                                        key={id}
                                        onClick={() => setTheme(id)}
                                        className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-xs font-medium transition ${theme === id ? "bg-[var(--surface)] text-[var(--text)] shadow-sm border border-[var(--border-ui)]" : "text-[var(--muted-text)] border border-transparent hover:text-[var(--text)]"}`}
                                    >
                                        <Icon className="h-3.5 w-3.5" />{label}
                                    </button>
                                ))}
                            </div>
                            <p className="mt-2 px-1 text-[10px] leading-4 text-[var(--subtle-text)]">Your choice is saved on this device and used across the whole site.</p>

                            <p className="text-[10px] text-[var(--subtle-text)] tracking-wide font-medium mb-3 mt-7">Exam</p>
                            <div className="bg-[var(--surface-raised)] border border-[var(--border-ui)] rounded-2xl divide-y divide-[var(--border-ui)] overflow-hidden">
                                <div className="flex items-center justify-between px-4 py-3.5">
                                    <div>
                                        <div className="text-[13px] font-medium text-[var(--text)]">Timed by default</div>
                                        <div className="text-[11px] text-[var(--subtle-text)] mt-0.5">Start sessions with timer on</div>
                                    </div>
                                    <button onClick={() => setSettingTimed(v => !v)} className={`relative w-9 h-5 rounded-full transition-colors duration-200 flex-shrink-0 ${settingTimed ? "bg-[#60a5fa]" : "bg-[var(--soft-fill-strong)]"}`}>
                                        <span className="absolute top-1 w-3 h-3 bg-white rounded-full shadow transition-all duration-200" style={{ left: settingTimed ? "20px" : "4px" }} />
                                    </button>
                                </div>
                                <div className="flex items-center justify-between px-4 py-3.5">
                                    <div>
                                        <div className="text-[13px] font-medium text-[var(--text)]">Classic interface</div>
                                        <div className="text-[11px] text-[var(--subtle-text)] mt-0.5">Default to PearsonVUE style</div>
                                    </div>
                                    <button onClick={() => setSettingClassic(v => !v)} className={`relative w-9 h-5 rounded-full transition-colors duration-200 flex-shrink-0 ${settingClassic ? "bg-[#60a5fa]" : "bg-[var(--soft-fill-strong)]"}`}>
                                        <span className="absolute top-1 w-3 h-3 bg-white rounded-full shadow transition-all duration-200" style={{ left: settingClassic ? "20px" : "4px" }} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default PracticeHub;
